import React, { useEffect } from 'react';
import { Box, Circle, Flex, Image, Text } from '@chakra-ui/react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { ImagePlaceholder } from './ImagePlaceholder';

const MotionBox = motion(Box);

export type PassNotificationProps = {
  open: boolean;
  autoHideMs?: number;
  onClose?: () => void;
  title?: string;
  subtitle?: string;
  imageUrl?: string;
  inset?: string;
  device?: 'apple' | 'google';
  highlightIcon?: boolean;
};

const springy = {
  type: 'spring' as const,
  stiffness: 560,
  damping: 42,
  mass: 0.8,
};

const shadow = '0 18px 40px rgba(0,0,0,0.28)';

export const PassNotification: React.FC<PassNotificationProps> = ({
  open,
  autoHideMs = 2200,
  onClose,
  title = 'Notification header',
  subtitle = 'Your message text here',
  imageUrl,
  inset,
  device,
  highlightIcon = false,
}) => {
  const prefersReduced = useReducedMotion();

  useEffect(() => {
    if (!open || !autoHideMs) return;
    const t = setTimeout(() => onClose?.(), autoHideMs);
    return () => clearTimeout(t);
  }, [open, autoHideMs, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <>
          {device === 'apple' && (
            <MotionBox
              position="absolute"
              inset={inset}
              zIndex={50}
              pointerEvents="none"
              _before={{
                content: '""',
                position: 'absolute',
                inset: '-18px',
                borderRadius: '15px',
                filter: 'blur(22px)',
              }}
              initial={{
                y: prefersReduced ? 0 : -28,
                opacity: 0,
                filter: prefersReduced ? undefined : 'blur(6px)',
                scale: prefersReduced ? 1 : 0.98,
              }}
              animate={{
                y: 0,
                opacity: 1,
                filter: 'blur(0px)',
                scale: prefersReduced ? 1 : [0.98, 1.02, 1],
                transition: prefersReduced
                  ? { duration: 0.2 }
                  : { ...springy, bounce: 0.2 },
              }}
              exit={{
                y: prefersReduced ? 0 : -16,
                opacity: 0,
                filter: prefersReduced ? undefined : 'blur(3px)',
                transition: { duration: 0.18 },
              }}
            >
              <Flex
                align="center"
                gap={3}
                px={3}
                py={2}
                borderRadius="15px"
                bg="#FFFFFF"
                backdropFilter="saturate(140%) blur(8px)"
                boxShadow={shadow}
                border="1px solid rgba(255,255,255,0.3)"
                pointerEvents="auto"
              >
                {imageUrl ? (
                  <Image
                    boxSize="28px"
                    borderRadius="8px"
                    src={imageUrl}
                    alt=""
                    {...(highlightIcon && {
                      outline: '2px solid',
                      outlineColor: 'orangePrimary',
                      outlineOffset: '1px',
                    })}
                  />
                ) : (
                  <ImagePlaceholder
                    boxSize="28px"
                    borderRadius="8px"
                    flexShrink={0}
                    data-testid={
                      highlightIcon ? 'notification-icon-highlight' : undefined
                    }
                    {...(highlightIcon && {
                      outline: '2px solid',
                      outlineColor: 'orangePrimary',
                      outlineOffset: '1px',
                    })}
                  />
                )}
                <Box minW="0" flex="1">
                  <Flex justify="space-between" align="center">
                    <Text
                      fontSize="sm"
                      lineHeight="1.1"
                      color="blackAlpha.900"
                      noOfLines={1}
                      textStyle="headingFamilyMediumApplePay"
                    >
                      {title}
                    </Text>
                    <Text
                      fontSize="xs"
                      color="blackAlpha.700"
                      ml={3}
                      flexShrink={0}
                      textStyle="headingFamilyRegularApplePay"
                    >
                      now
                    </Text>
                  </Flex>
                  <Text
                    fontSize="xs"
                    color="blackAlpha.800"
                    noOfLines={1}
                    textStyle="headingFamilyRegularApplePay"
                  >
                    {subtitle}
                  </Text>
                </Box>
              </Flex>
            </MotionBox>
          )}
          {device === 'google' && (
            <>
              <MotionBox
                position="absolute"
                inset={inset}
                zIndex={50}
                pointerEvents="none"
                _before={{
                  content: '""',
                  position: 'absolute',
                  inset: '-18px',
                  borderRadius: '15px',
                  filter: 'blur(22px)',
                }}
                initial={{
                  y: prefersReduced ? 0 : -28,
                  opacity: 0,
                  filter: prefersReduced ? undefined : 'blur(6px)',
                  scale: prefersReduced ? 1 : 0.98,
                }}
                animate={{
                  y: 0,
                  opacity: 1,
                  filter: 'blur(0px)',
                  scale: prefersReduced ? 1 : [0.98, 1.02, 1],
                  transition: prefersReduced
                    ? { duration: 0.2 }
                    : { ...springy, bounce: 0.2 },
                }}
                exit={{
                  y: prefersReduced ? 0 : -16,
                  opacity: 0,
                  filter: prefersReduced ? undefined : 'blur(3px)',
                  transition: { duration: 0.18 },
                }}
              >
                <Box
                  position="absolute"
                  inset={inset}
                  zIndex={50}
                  pointerEvents="none"
                >
                  <Box
                    borderRadius="15px"
                    minH="60px"
                    bg="#FFFFFF"
                    border="1px solid rgba(0,0,0,0.06)"
                    boxShadow="0 12px 28px rgba(0,0,0,0.18)"
                    pointerEvents="auto"
                  >
                    <Flex direction="column" py={3} pl={6} pr={4}>
                      <Flex
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Flex direction="column" gap={0.5}>
                          <Flex gap={0.5} alignContent="center">
                            <Text fontSize="11px" fontWeight="semibold">
                              New message
                            </Text>
                            <Text fontSize="10px">•</Text>
                            <Text fontSize="10px">now</Text>
                          </Flex>
                          <Text fontSize="11px">Tap to view pass</Text>
                        </Flex>
                        <Flex direction="row" gap={1}>
                          <Flex gap={1} alignItems="center">
                            <Circle size="32px" border="1px solid #D3D3D3">
                              {imageUrl ? (
                                <Image
                                  borderRadius="100%"
                                  boxSize="full"
                                  objectFit="cover"
                                  src={imageUrl}
                                  alt=""
                                />
                              ) : (
                                <ImagePlaceholder
                                  borderRadius="100%"
                                  boxSize="full"
                                />
                              )}
                            </Circle>
                          </Flex>
                          <ChevronDownIcon
                            fontSize="16px"
                            color="blackAlpha.800"
                          />
                        </Flex>
                      </Flex>
                      <Flex direction="column" justifyContent="start" gap={1}>
                        <Text fontSize="11px" fontWeight="semibold" pt={2}>
                          Notification settings
                        </Text>
                      </Flex>
                    </Flex>
                  </Box>
                </Box>
              </MotionBox>
            </>
          )}
        </>
      )}
    </AnimatePresence>
  );
};
